const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.deepchat.se'

export interface BillingStatus {
  subscription_status: string
  subscription_plan?: string | null
  subscription_plan_id?: number | null
  plan_name?: string | null
  price_sek?: number | null
  billing_period?: 'monthly' | 'yearly' | null
  billing_status: 'active' | 'expiring_soon' | 'expired' | 'suspended'
  trial_end_date?: string | null
  billing_period_start?: string | null
  billing_period_end?: string | null
  is_active: boolean
  max_agents: number
  current_agent_count: number
  payment_link?: string | null
}

export interface SubscriptionPlan {
  id: number
  name: string
  description: string
  price_sek: number
  billing_period: 'monthly' | 'yearly'
  max_agents: number
  features: string[]
  stripe_price_id?: string | null
}

class BillingService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token')
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    }
  }

  async getStatus(): Promise<BillingStatus> {
    const response = await fetch(`${API_BASE_URL}/api/billing/status`, {
      headers: this.getAuthHeaders()
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || data.error || 'Failed to load billing status')
    }
    return data.data
  }

  async getPlans(): Promise<SubscriptionPlan[]> {
    const response = await fetch(`${API_BASE_URL}/api/plans`)
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to load plans')
    }
    return (data.data || []).map((plan: any) => ({
      ...plan,
      price_sek: Number(plan.price_sek || 0),
      max_agents: Number(plan.max_agents || 0),
      features: Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '[]')
    }))
  }

  async createCheckout(planId: number): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/api/billing/checkout`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ plan_id: planId })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create Stripe checkout')
    }
    return data.data.url
  }

  async confirmCheckout(sessionId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/billing/confirm`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ session_id: sessionId })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to confirm payment')
    }
  }
}

export const billingService = new BillingService()
